import { Access, Aggregate, Bin, DatePart, DateTrunc, Difference, Expr, ExprEnv, ExprForm, Generic, ParseNode, Parser, ParseTree, PctDifference, PctOfTotal, } from '@mode-switch/express';
import { Pipeline } from './Pipeline';
import { Resolver } from './Resolver';
export var Compiler;
(function (Compiler) {
    let Result;
    (function (Result) {
        let Type;
        (function (Type) {
            Type["Success"] = "SUCCESS";
            Type["ParseFailure"] = "PARSE_FAILURE";
            Type["ResolveFailure"] = "RESOLVE_FAILURE";
        })(Type = Result.Type || (Result.Type = {}));
        let Success;
        (function (Success) {
            function make(pipeline, expr) {
                const { root } = expr;
                const node = Pipeline.ExprNodes.get(pipeline, root);
                if (node == null) {
                    throw new Error(`root node not found`);
                }
                return { node, expr, type: "SUCCESS" };
            }
            Success.make = make;
        })(Success = Result.Success || (Result.Success = {}));
        let ParseFailure;
        (function (ParseFailure) {
            function make(source, ...errors) {
                return { source, errors, type: "PARSE_FAILURE" };
            }
            ParseFailure.make = make;
        })(ParseFailure = Result.ParseFailure || (Result.ParseFailure = {}));
        let ResolveFailure;
        (function (ResolveFailure) {
            function make(expr, ...errors) {
                return { expr, errors, type: "RESOLVE_FAILURE" };
            }
            ResolveFailure.make = make;
        })(ResolveFailure = Result.ResolveFailure || (Result.ResolveFailure = {}));
    })(Result = Compiler.Result || (Compiler.Result = {}));
    function compile(catalog, pipeline, model, viewName, source, exprEnv) {
        let tree;
        let parsed;
        try {
            const result = Parser.parse(source);
            if (result.type === "FAILURE") {
                return Result.ParseFailure.make(source, result.error);
            }
            tree = result.tree;
            parsed = Expr.Parsed.make(result.root);
        }
        catch (error) {
            if (error instanceof Error) {
                return Result.ParseFailure.make(source, error);
            }
            else if (typeof error === 'string') {
                return Result.ParseFailure.make(source, new Error(error));
            }
            else {
                return Result.ParseFailure.make(source, new Error(`Compile: failed to parse source ${source}`));
            }
        }
        try {
            exprEnv = ExprEnv.make(exprEnv);
            const resolver = Resolver.make(catalog, model, viewName, exprEnv);
            const resolved = Resolver.resolve(pipeline, tree, resolver);
            if (resolved instanceof Error) {
                return Result.ResolveFailure.make(parsed, resolved);
            }
            const root = ParseTree.root(tree);
            if (root == null) {
                return Result.ResolveFailure.make(parsed, new Error(`Could not resolve ${source}`));
            }
            return compiled(parsed, resolved);
        }
        catch (error) {
            if (error instanceof Error) {
                return Result.ResolveFailure.make(parsed, error);
            }
            else if (typeof error === 'string') {
                return Result.ResolveFailure.make(parsed, new Error(error));
            }
            else {
                return Result.ParseFailure.make(source, new Error(`Compile: failed to resolve source ${source}`));
            }
        }
    }
    Compiler.compile = compile;
    function compiled(parsed, resolved) {
        const { root } = parsed;
        const type = Pipeline.DataTypes.get(resolved, root);
        const role = Pipeline.DataRoles.get(resolved, root);
        const stage = Pipeline.ExprStages.get(resolved, root);
        if (type == null) {
            return Result.ResolveFailure.make(parsed, new Error(`Cannot determine data type`));
        }
        if (stage == null) {
            return Result.ResolveFailure.make(parsed, new Error(`Cannot determine expression stage`));
        }
        return build(parsed, resolved, stage, { type, role });
    }
    function build(parsed, resolved, stage, data) {
        let comp;
        switch (parsed.form) {
            case ExprForm.Access: {
                comp = Access.Compiled.make(parsed, stage, data);
                break;
            }
            case ExprForm.Bin: {
                comp = Bin.Compiled.make(parsed, stage, data);
                break;
            }
            case ExprForm.DatePart: {
                comp = DatePart.Compiled.make(parsed, stage, data);
                break;
            }
            case ExprForm.DateTrunc: {
                comp = DateTrunc.Compiled.make(parsed, stage, data);
                break;
            }
            case ExprForm.Aggregate: {
                comp = Aggregate.Compiled.make(parsed, stage, data);
                break;
            }
            case ExprForm.Difference: {
                const result = compiled(parsed.expr, resolved);
                if (result.type !== "SUCCESS") {
                    return result;
                }
                else {
                    const { expr } = result;
                    switch (expr.form) {
                        case ExprForm.Access:
                        case ExprForm.Aggregate:
                        case ExprForm.Generic: {
                            comp = Difference.Compiled.make(parsed, expr, stage, data);
                            break;
                        }
                    }
                }
                break;
            }
            case ExprForm.PctDifference: {
                const result = compiled(parsed.expr, resolved);
                if (result.type !== "SUCCESS") {
                    return result;
                }
                else {
                    const { expr } = result;
                    switch (expr.form) {
                        case ExprForm.Access:
                        case ExprForm.Aggregate:
                        case ExprForm.Generic: {
                            comp = PctDifference.Compiled.make(parsed, expr, stage, data);
                            break;
                        }
                    }
                }
                break;
            }
            case ExprForm.PctOfTotal: {
                const result = compiled(parsed.expr, resolved);
                if (result.type !== "SUCCESS") {
                    return result;
                }
                else {
                    const { expr } = result;
                    switch (expr.form) {
                        case ExprForm.Access:
                        case ExprForm.Aggregate: {
                            comp = PctOfTotal.Compiled.make(parsed, expr, stage, data);
                            break;
                        }
                    }
                }
                break;
            }
            default: {
                comp = Generic.Compiled.make(parsed, stage, data);
                break;
            }
        }
        if (comp == null) {
            const source = ParseNode.toString(parsed.root);
            return Result.ResolveFailure.make(parsed, new Error(`Cannot determine the compiled form of ${source}`));
        }
        return Result.Success.make(resolved, comp);
    }
})(Compiler || (Compiler = {}));
